import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useContext, useEffect } from 'react';

import DividerWithText from '~components/DividerWithText/DividerWithText';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { Order } from '~hooks/useOrders/models';

import { OrderTicketImportAction } from './OrderTicketImportAction';
import OrderTicketFilter from './tickets-table/Filters/OrderTicketFilter';
import OrderTicketSummary from './tickets-table/OrderTicketSummary';
import TableHeader from './tickets-table/TableHeader';
import TableRow from './tickets-table/TableRow';
import { columns, gridTemplateColumns } from './tickets-table/tickets-table-columns';
import TicketsQueryContext from './tickets-table/TicketsQueryContext/TicketsQueryContext';
import { TicketsQueryContextProvider } from './tickets-table/TicketsQueryContext/TicketsQueryContext';

interface TicketsTableProps {
  order: Order;
}

const TicketsTable = ({ order }: TicketsTableProps) => {
  const theme = useTheme();

  return (
    <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
      <TicketsQueryContextProvider orderId={order.id}>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="h6">{t('dispatch.tickets_table.title')}</Typography>
          <OrderTicketImportAction order={order} />
        </Box>
        <OrderTicketFilter sx={{ mx: 1, mb: 1 }} />
        <OrderTicketSummary order={order} />
        <TicketsTableContainer />
        <Footer />
      </TicketsQueryContextProvider>
    </Box>
  );
};

const TicketsTableContainer = () => {
  const { data, isLoading, isFetching, hasMore, fetchMore } =
    useContext(TicketsQueryContext);

  // Automatically fetch all tickets
  useEffect(() => {
    if (hasMore && !isLoading && !isFetching) {
      fetchMore();
    }
  }, [hasMore, isLoading, isFetching, fetchMore]);
  const loading = isFetching || isLoading;

  return (
    <Box
      sx={{
        display: 'grid',
        gridAutoRows: 'auto',
        gridTemplateColumns,
        gridTemplateRows: '35px',
      }}
    >
      {columns.map((column) => (
        <TableHeader key={column.key} title={column.title} sx={column.sx} />
      ))}

      {data.map((ticket) => (
        <TableRow key={ticket.id} ticket={ticket} />
      ))}

      {loading && (
        <Box
          sx={{
            gridColumn: `span ${columns.length}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 2,
          }}
        >
          <LoadingSpinner isVisible />
        </Box>
      )}
    </Box>
  );
};

const Footer = () => {
  const { data, fetchReason, hasMore } = useContext(TicketsQueryContext);

  if (hasMore || fetchReason || data.length > 0) {
    return null;
  }

  return <DividerWithText text={t('dispatch.tickets_table.no_tickets')} />;
};

export default TicketsTable;
